import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing, Star, BellOff } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";
import { usePT } from "@/lib/pt";

const KEY = "ks_partner_push";

export const PartnerAlerts = ({ token, vapid, compact }) => {
  const { pt } = usePT();
  const [on, setOn] = useState(pushSupported() && Notification.permission === "granted" && localStorage.getItem(KEY) === token);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (pushSupported() && Notification.permission === "granted" && vapid) enablePush(vapid, `/partner/${token}/push/subscribe`).then(() => { localStorage.setItem(KEY, token); setOn(true); }).catch(() => {});
  }, [token, vapid]);
  if (!pushSupported()) return null;
  const turnOn = async () => {
    setBusy(true);
    try { await enablePush(vapid, `/partner/${token}/push/subscribe`); localStorage.setItem(KEY, token); setOn(true); await api.post(`/partner/${token}/push/test`); toast.success("Alerts on. We sent you a test notification."); }
    catch (e) { toast.error(e.response ? errMsg(e) : e.message); } finally { setBusy(false); }
  };
  if (on && compact) return null;
  if (on) return (
    <button onClick={() => api.post(`/partner/${token}/push/test`).then(() => toast.success("Test alert sent")).catch((e) => toast.error(errMsg(e)))}
      className="flex w-full items-center gap-2 rounded-2xl bg-[#E3F4EA] p-3 text-left text-sm font-semibold text-[#1F7A50]" data-testid="partner-alerts-on">
      <BellRing className="h-4 w-4" />Request alerts are on, even when the app is closed. Tap to test.</button>
  );
  return (
    <div className="space-y-2 rounded-3xl border-2 border-[#E05C3A] bg-[#FDF1EC] p-4" data-testid="partner-alerts-off">
      <p className="flex items-center gap-2 text-base font-bold text-[#B4401F]"><BellOff className="h-6 w-6" />{pt("Turn on alerts")}</p>
      {!compact && <p className="text-xs text-[#7A3A25]">Get a loud notification with Accept / Decline for every new pickup, even when the app is closed or the screen is off.</p>}
      <button onClick={turnOn} disabled={busy} className="h-12 w-full rounded-2xl bg-[#E05C3A] text-base font-bold text-white disabled:opacity-50" data-testid="partner-alerts-enable-btn">{busy ? pt("Turning on…") : pt("Turn on")}</button>
    </div>
  );
};

export const StarBadge = ({ small, testid = "star-badge" }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#F5C451] to-[#E0A43A] font-extrabold text-[#5A3B00] shadow-sm ${small ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs"}`}>
    <Star className={`${small ? "h-3 w-3" : "h-3.5 w-3.5"} fill-current`} />Star Partner</span>
);

export const StarBanner = ({ star, count }) => (star ? (
  <div className="ks-rise relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FFF4D6] to-[#F5C451] p-4" data-testid="partner-star-banner">
    <Star className="absolute -right-4 -top-4 h-24 w-24 fill-white/40 text-white/0" />
    <StarBadge />
    <p className="mt-2 font-display text-lg font-extrabold text-[#5A3B00]">You're a Star Partner this week!</p>
    <p className="text-xs text-[#6B4A00]">Score {star.score}% with {star.completed} pickups last week. You get nearby pickups first until Sunday.{count > 1 && ` Star weeks earned: ${count}.`}</p>
  </div>
) : (
  <p className="rounded-2xl bg-white p-3 text-xs text-[#4A6358]" data-testid="partner-star-goal"><Star className="mr-1 inline h-3.5 w-3.5 text-[#E0A43A]" />Become a <b>Star Partner</b>: score 85%+, 5+ pickups in a week and 4.5+ rating. Stars get nearby pickups first.</p>
));
